import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUser } from "@/contexts/UserContext";
import { formatPhone } from "@/utils/helper";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { useState, useEffect } from "react";
import { Check } from "lucide-react";

interface Service {
  id: number;
  name: string;
  description: string;
  product_type: string;
}

type FormValues = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  job_title: "SERVER" | "MANAGER" | "OWNER";
  employee_pin: string;
  branch: number;
  service_provided: number[];
};

const formSchema = z.object({
  first_name: z.string().nonempty("First Name is required"),
  last_name: z.string().nonempty("Last Name is required"),
  phone: z.string().regex(/^\d{10}$/, {
    message: "Invalid phone number, must be 10 digits",
  }),
  email: z.string().email(),
  job_title: z.enum(["SERVER", "MANAGER", "OWNER"], {
    message: "Invalid job title, must be Server or Manager",
  }),
  employee_pin: z
    .string()
    .length(4, "Employee ID must be exactly 4 characters"),
  branch: z.number(),
  service_provided: z.array(z.number()),
}) satisfies z.ZodType<FormValues>;

const AddEmployee = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [services, setServices] = useState<Service[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      job_title: "SERVER",
      branch: user?.selectedBranch?.id || 0,
      employee_pin: "",
      service_provided: [],
    },
  });

  useEffect(() => {
    const fetchServices = async () => {
      if (!axiosInstance || !user?.selectedBranch?.id) return;

      try {
        // Fetch services with proper query parameters
        const response = await axiosInstance.get(
          `/api/v1/product?branch=${user.selectedBranch.id}&product_type=Services&is_active=true&page_size=100`,
        );
        console.log("Services response:", response.data); // Debug log
        if (response.data.results) {
          setServices(response.data.results);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        toast.error("Failed to fetch available services");
      }
    };

    fetchServices();
  }, [axiosInstance, user?.selectedBranch?.id]);

  const onSubmit = async (data: FormValues) => {
    if (loading || !axiosInstance) return;
    console.log("Data: ", data);

    try {
      const employeeData = {
        first_name: data.first_name,
        last_name: data.last_name,
        phone: `+1${data.phone.replace(/\D/g, "")}`,
        email: data.email,
        job_title: data.job_title,
        employee_pin: data.employee_pin,
        branch: data.branch,
        service_provided: data.service_provided,
      };

      const res = await axiosInstance.post("/api/v1/employee/", employeeData);

      toast.success(
        `Employee created successfully with passkey: ${res.data.employee_pin}`,
      );
      navigate("/employee");
    } catch (error: any) {
      console.log("Error: ", error);
      if (axios.isAxiosError(error)) {
        const errorObj = error?.response?.data;
        const errorKey = Object.keys(errorObj)[0];
        const errorData = errorObj[errorKey as keyof typeof errorObj];
        toast.error(`Error in creating employee: ${errorData}`);
      } else {
        toast.error("An unexpected error occurred.");
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-2">
      <h1 className="mb-4 text-xl font-bold">Add Employee</h1>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col space-y-6"
        >
          <div className="rounded-lg bg-white p-4 shadow">
            <h1 className="font-semibold lg:text-lg">Employee Information</h1>
            <div className="mt-4 space-y-5">
              <div className="w-full space-y-2">
                <Label htmlFor="name">
                  Employee Name
                  <span className="text-red-500">*</span>
                </Label>
                <div className="grid w-full grid-cols-2 space-x-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <Input {...field} placeholder="First Name" required />
                        <FormMessage>
                          {form.formState.errors.first_name?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <Input {...field} placeholder="Last Name" required />
                        <FormMessage>
                          {form.formState.errors.last_name?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="grid-col-1 grid w-full lg:grid-cols-2 lg:space-x-4">
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="phone">
                          Phone
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <Input
                          {...field}
                          placeholder="Phone"
                          required
                          value={formatPhone(field.value)}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            field.onChange(value);
                          }}
                        />
                        <FormMessage>
                          {form.formState.errors.phone?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="email">
                          Email
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <Input {...field} placeholder="Email" required />
                        <FormMessage>
                          {form.formState.errors.email?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="grid-col-1 grid w-full lg:grid-cols-2 lg:space-x-4">
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="job_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="job_title">
                          Job Title
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <select
                          {...field}
                          className="form-select mt-1 block h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                          defaultValue="SERVER"
                        >
                          <option value="SERVER">Server</option>
                          <option value="MANAGER">Manager</option>
                        </select>
                        <FormMessage>
                          {form.formState.errors.job_title?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="employee_pin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="employee_pin">
                          Employee Pin
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <Input
                          {...field}
                          placeholder="Employee Pin"
                          required
                          type="number"
                          maxLength={4}
                          minLength={4}
                        />
                        <FormMessage>
                          {form.formState.errors.employee_pin?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow">
            <FormField
              control={form.control}
              name="service_provided"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-lg font-semibold">
                    Services Provided
                    {field.value.length === 0 && (
                      <span className="text-red-500">*</span>
                    )}
                  </FormLabel>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {services.map((service) => (
                      <div
                        key={service.id}
                        className={`cursor-pointer rounded-lg border p-4 transition-all hover:border-red-500 ${
                          field.value.includes(service.id)
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200"
                        }`}
                        onClick={() => {
                          const newValue = field.value.includes(service.id)
                            ? field.value.filter((id) => id !== service.id)
                            : [...field.value, service.id];
                          field.onChange(newValue);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-medium">{service.name}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {service.description}
                            </p>
                          </div>
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                              field.value.includes(service.id)
                                ? "border-red-500 bg-red-500 text-white"
                                : "border-gray-300"
                            }`}
                          >
                            {field.value.includes(service.id) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {services.length === 0 && (
                    <p className="text-center text-gray-500">
                      No services available for this branch
                    </p>
                  )}
                  <FormMessage>
                    {form.formState.errors.service_provided?.message}
                  </FormMessage>
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              onClick={() => navigate("/employee")}
              variant="secondary"
              className="bg-gray-500 text-white hover:text-gray-600"
              type="button"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className="bg-red-500 text-white hover:bg-red-600"
              type="submit"
            >
              Add Employee
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AddEmployee;
